import React from "react";

import { Grid } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import {
  Edit,
  email,
  FormDataConsumer,
  FormTab,
  maxLength,
  minLength,
  regex,
  required,
  TabbedForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
} from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { BackButton } from "../../../components/common/BackButton";
import { CarrierIdentifiersInput } from "../../../components/carrier/CarrierIdentifiersInput";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { AclType, REGEX_PHONE } from "../../../utils/constants";
import { isCarrierProfileSelfManaged } from "../../../utils/carrierUserUtils";
import { getBackPath } from "../../../utils/utils";
import { ValidateSaveButton } from "./ValidateSaveButton";
import { ArchiveRestoreButton } from "./ArchiveRestoreButton";
import { DeepDeleteButton } from "./DeepDeleteButton";
import { CarrierUsersTab } from "./CarrierUsersTab";
import { CarrierFmcsaAside } from "./CarrierFmcsaAside";
import { SendPrimaryInviteButton } from "./SendPrimaryInviteButton";

const validateRequired = required();
// The field is editable only while empty and locks once saved, and whatever it
// holds becomes the address the primary dispatch invite is sent to. A typo
// therefore strands the carrier's undeletable primary account somewhere nobody
// can reach and nobody can correct — the native type="email" check does not
// catch it, since ValidateSaveButton submits programmatically.
const validateEmail = email("Must be a valid email address");
// Optional, as on create — and a carrier enrolled from the kiosk can arrive
// without a number at all, which this form must be able to save unchanged.
const validatePhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];

// Why the fields are read-only, said once at the top of the form rather than on
// each field. The controls that are still the yard's — Send Invite, Archive,
// Delete — are deliberately left enabled, so the note names them.
const SELF_MANAGED_NOTICE =
  "This carrier maintains its own profile. Its dispatchers edit the business name, contacts and ID numbers from the carrier portal, on one company record shared across every transload it works — a yard editing them here would overwrite what the company keeps up to date. Inviting, archiving and deleting the carrier are still yours.";

const CarrierEditForm = ({ record, selfManaged, ...props }) => {
  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container spacing={1}>
          {selfManaged && (
            <Grid item xs={12}>
              <Alert severity="info">{SELF_MANAGED_NOTICE}</Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextInput
              source="businessName"
              label="Business Name"
              variant="outlined"
              fullWidth
              disabled={selfManaged}
              validate={validateRequired}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="dbaName"
              label="Doing business as"
              helperText="Trading name, if different. Drivers can search it at the kiosk."
              variant="outlined"
              fullWidth
              disabled={selfManaged}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="email"
              type="email"
              label="Email"
              variant="outlined"
              fullWidth
              parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
              validate={validateEmail}
              // The address anchors the carrier's primary user, so once set it
              // stays put. Kiosk-created carriers arrive without one — adding
              // it here (then Send Invite) is how their onboarding finishes,
              // which is also the only state in which this form still owns it.
              disabled={selfManaged || Boolean(record?.email)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="phone"
              type="tel"
              label="Phone"
              variant="outlined"
              fullWidth
              disabled={selfManaged}
              validate={validatePhone}
            />
          </Grid>
          <Grid item xs={12}>
            <CarrierIdentifiersInput
              disabled={selfManaged}
              helperText={
                selfManaged
                  ? "Kept by the carrier, in the carrier portal."
                  : undefined
              }
            />
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const CarrierEditActions = ({ data, ...props }) => (
  <TopToolbar>
    <SendPrimaryInviteButton record={data} />
    <BackButton {...props} />
  </TopToolbar>
);

const CarrierEditToolbar = ({
  record,
  appUser,
  setAppUser,
  isMultiTenant,
  backPath,
  selfManaged,
  ...props
}) => {
  return (
    <Toolbar style={{ justifyContent: "space-between" }} {...props}>
      {/* Left out rather than disabled: with every field read-only there is
          nothing for a save to carry, and a greyed-out Save reads as a bug in
          the form instead of as the carrier's own record. */}
      {!selfManaged && (
        <ValidateSaveButton {...props} record={record} backPath={backPath} />
      )}
      {/* marginLeft on top of the toolbar's space-between: a self-managed
          carrier has no Save, and with one item left to distribute
          space-between would slide the yard's own controls over to where Save
          sits on every other carrier. */}
      <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
        {/* Delete: permanent DB removal. Only enabled when the carrier has no linked
          users (active or inactive). Use for carriers created in error.*/}
        <DeepDeleteButton record={record} />
        {/*  Archive: sets Carrier.active="false". Reversible via Restore. Users are
          untouched — access is blocked at login via the carrier.active check in
          setupCarrierData (App.js). Use to suspend an operational carrier. */}
        <ArchiveRestoreButton record={record} />
      </div>
    </Toolbar>
  );
};

export const CarrierEdit = (props) => {
  const { appUser, setAppUser, isAuthorized, isMultiTenant } = useUser();
  const { record } = useEditController(props);
  const backPath = isAuthorized(AclType.USERS) ? getBackPath(props) : ["/"];
  // Undefined while the record loads, which reads as not self-managed and would
  // flash an editable form. The fields below are only rendered once `record` is
  // there, so the first paint the manager sees is already the right one.
  const selfManaged = isCarrierProfileSelfManaged(record);

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Carrier" />}
      mutationMode="pessimistic"
      actions={<CarrierEditActions backPath={backPath} />}
      // The register's own record of this carrier, beside the yard's. Edit passes
      // the record in, so the panel describes whatever number is saved rather
      // than whatever the form is mid-way through typing.
      aside={<CarrierFmcsaAside />}
    >
      <TabbedForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <CarrierEditToolbar
            record={record}
            appUser={appUser}
            setAppUser={setAppUser}
            isMultiTenant={isMultiTenant}
            backPath={backPath}
            selfManaged={selfManaged}
          />
        }
      >
        <FormTab label="Company">
          <CarrierEditForm record={record} selfManaged={selfManaged} />
        </FormTab>
        <FormTab label="Users">
          <CarrierUsersTab
            record={record}
            backPath={backPath}
            location={props.location}
          />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};
